import { usePage } from '@inertiajs/react';
import { Crisp } from 'crisp-sdk-web';
import { useEffect } from 'react';

import { getDisplayName } from '@/helpers/account';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { SharedProps } from '@/types';

const CRISP_WEBSITE_ID = 'febf124e-40a0-4d07-a72a-0fb6d2351f07';

export default function useCrispChat() {
  const { props, url } = usePage<SharedProps>();
  const { current_account: currentAccount, current_organization: currentOrganization, locale } = props;
  const { isMobile } = useMediaQuery();
  const nickname = currentAccount ? getDisplayName(currentAccount) : null;

  // Load Crisp and surface pro user context in the conversation's "Visitor data" panel.
  // Writing to the People profile (company/segments) needs a paid plan, so support gets the
  // useful links via session data instead — see Crisp::People for the server-side path.
  // The chat bubble is hidden on mobile, where it overlaps the message send button.
  useEffect(() => {
    if (!currentAccount?.email || !nickname || isMobile) return;

    if (!Crisp.isCrispInjected()) {
      Crisp.configure(CRISP_WEBSITE_ID, { locale });
      // Crisp also checks for a phone on its own side, so its bubble never covers the send button.
      Crisp.setHideOnMobile(true);
    }

    Crisp.user.setEmail(currentAccount.email);
    Crisp.user.setNickname(nickname);

    const origin = window.location.origin;
    const visitorData: Record<string, string> = {
      admin_url: `${origin}/avo/resources/accounts/${currentAccount.id}`,
    };

    if (currentOrganization) {
      visitorData.organization = currentOrganization.name;
      visitorData.organization_url = `${origin}/pro/conversations?organization_id=${currentOrganization.id}`;
    }

    Crisp.session.setData(visitorData);
  }, [currentAccount?.email, currentAccount?.id, nickname, currentOrganization, locale, isMobile]);

  // Show on /organizations/ pages and on the inbox list, but hide over the conversation chat
  // (the bubble overlaps the message send button) and on mobile
  const isOrganizationPage = url.includes('/organizations/');
  const isConversationShowPage = /^\/pro\/conversations\/./.test(url);
  const isConversationsIndexPage = url.startsWith('/pro/conversations') && !isConversationShowPage;
  const showCrisp = (isOrganizationPage || isConversationsIndexPage) && !isMobile;

  useEffect(() => {
    if (!Crisp.isCrispInjected()) return;

    if (showCrisp) {
      Crisp.chat.show();
    } else {
      Crisp.chat.hide();
    }
  }, [showCrisp]);
}
